import { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ModuleItem } from '../reference-data/types';
import { foldSpelling, nameMatches } from '../reference-data/topicSearch';
import './TopicSelector.css';

interface TopicSelectorProps {
  /** All modules for the current subject (all levels) */
  modules: ModuleItem[];
  /** Currently selected module ID (module, chapter, or topic) */
  value: string;
  /** Called when user selects a module, chapter, or topic */
  onChange: (moduleId: string) => void;
  /** Placeholder text */
  placeholder?: string;
  disabled?: boolean;
  /** Soft-disable: the control stays visible but interacting with it doesn't open
   *  the dropdown — instead `onBlockedInteraction` fires (e.g. to prompt the user
   *  to pick a subject first). Distinct from `disabled`, which greys it out. */
  blocked?: boolean;
  onBlockedInteraction?: () => void;
  className?: string;
  /** Which levels may actually be CHOSEN. Omitted ⇒ all three, the filtering
   *  behaviour every caller had: picking a module or chapter pulls all of its
   *  descendants' questions.
   *
   *  The teacher's document import passes `['chapter', 'topic']`: there the pick is also
   *  the filing destination for a question the classifier could not place, and
   *  a whole module is too broad to file under (a chapter files only when it
   *  holds one topic — see resolveQuestionModule). Unselectable levels are still
   *  rendered: they are the tree the rest hangs off, and hiding them would leave
   *  a flat list with no way to tell two similarly-named entries apart. */
  selectableLevels?: Array<'module' | 'chapter' | 'topic'>;
  /** Keep `modules` in the order given instead of sorting A→Z — for a list that
   *  already has a reading order of its own (a curriculum book's contents). */
  preserveOrder?: boolean;
}

const getLevel = (m: ModuleItem) => m.moduleLevel ?? (m.isTopModule ? 'module' : 'chapter');
const byNameAZ = (a: ModuleItem, b: ModuleItem) => a.displayName.localeCompare(b.displayName);

function pushTo<T>(map: Map<string, T[]>, key: string, val: T) {
  const arr = map.get(key);
  if (arr) arr.push(val);
  else map.set(key, [val]);
}

interface ChapterGroup { chapter: ModuleItem; topics: ModuleItem[] }
/** A top-level entry: a real module (with its chapters), or an orphan chapter
 *  (one with no parent module) promoted to the top level so nothing is hidden. */
interface ModuleGroup { module: ModuleItem | null; chapters: ChapterGroup[] }

/**
 * TopicSelector — typeahead/autocomplete over the full Module → Chapter → Topic
 * tree. Every level is selectable (selecting a module/chapter pulls all of its
 * descendants' questions — the backend expands via getModuleWithChildren).
 * Entries are sorted A→Z at every level. Filters as the user types (client-side).
 */
export function TopicSelector({ modules, value, onChange, placeholder = 'Search topics...', disabled, blocked, onBlockedInteraction, className, selectableLevels, preserveOrder }: TopicSelectorProps) {
  const canSelect = (m: ModuleItem) => !selectableLevels || selectableLevels.includes(getLevel(m));
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Dropdown is rendered in a portal (fixed positioning) so it escapes any
  // scroll/overflow clipping from an ancestor — e.g. a Modal's scrollable body.
  const [pos, setPos] = useState<{ left: number; top: number; width: number } | null>(null);

  // Build the module → chapter → topic tree, sorted A→Z at every level (or in
  // the order given, with preserveOrder).
  const grouped = useMemo<ModuleGroup[]>(() => {
    const position = new Map(modules.map((m, i) => [m.id, i]));
    const byName = preserveOrder
      ? (a: ModuleItem, b: ModuleItem) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0)
      : byNameAZ;
    const topicsByChapter = new Map<string, ModuleItem[]>();
    for (const m of modules) {
      if (getLevel(m) === 'topic' && m.parentModuleId) pushTo(topicsByChapter, m.parentModuleId, m);
    }
    for (const topics of topicsByChapter.values()) topics.sort(byName);
    const chapterGroup = (ch: ModuleItem): ChapterGroup => ({ chapter: ch, topics: topicsByChapter.get(ch.id) ?? [] });

    const moduleEntries = modules.filter(m => getLevel(m) === 'module');
    const moduleIds = new Set(moduleEntries.map(m => m.id));

    const chaptersByModule = new Map<string, ModuleItem[]>();
    const orphanChapters: ModuleItem[] = [];
    for (const ch of modules.filter(m => getLevel(m) === 'chapter')) {
      if (ch.parentModuleId && moduleIds.has(ch.parentModuleId)) pushTo(chaptersByModule, ch.parentModuleId, ch);
      else orphanChapters.push(ch); // chapter with no (resolvable) parent module
    }

    const groups: ModuleGroup[] = [
      ...moduleEntries.map(mod => ({
        module: mod,
        chapters: (chaptersByModule.get(mod.id) ?? []).sort(byName).map(chapterGroup),
      })),
      ...orphanChapters.map(ch => ({ module: null, chapters: [chapterGroup(ch)] })),
    ];
    // Sort the top level by the module, or by the orphan chapter itself.
    const top = (g: ModuleGroup) => (g.module ?? g.chapters[0].chapter);
    groups.sort((a, b) => byName(top(a), top(b)));
    return groups;
  }, [modules, preserveOrder]);

  // Filter by query — a match at any level keeps that branch (with its ancestors
  // for context). A module/chapter name match keeps everything beneath it.
  const filtered = useMemo(() => {
    if (!query.trim()) return grouped;
    // Spelling-folded, so "Factorisation" finds "Factorization" — see topicSearch.
    const q = foldSpelling(query);
    const result: ModuleGroup[] = [];
    for (const group of grouped) {
      const moduleMatch = group.module ? nameMatches(group.module.displayName, q) : false;
      const chapters: ChapterGroup[] = [];
      for (const cg of group.chapters) {
        const chapterMatch = nameMatches(cg.chapter.displayName, q);
        if (moduleMatch || chapterMatch) {
          chapters.push(cg); // whole chapter (all topics)
        } else {
          const topics = cg.topics.filter(t => nameMatches(t.displayName, q));
          if (topics.length > 0) chapters.push({ chapter: cg.chapter, topics });
        }
      }
      if (moduleMatch || chapters.length > 0) result.push({ module: group.module, chapters });
    }
    return result;
  }, [grouped, query]);

  // Resolve display name for the current value, prefixing parents so the choice
  // is unambiguous: "Module - Chapter - Topic" / "Module - Chapter" / "Module".
  const selectedLabel = useMemo(() => {
    if (!value) return '';
    const mod = modules.find(m => m.id === value);
    if (!mod) return '';
    const nameOf = (id?: string) => (id ? modules.find(m => m.id === id)?.displayName : undefined);
    const level = getLevel(mod);
    if (level === 'topic') {
      const chapter = modules.find(m => m.id === mod.parentModuleId);
      const moduleName = nameOf(chapter?.parentModuleId);
      return [moduleName, chapter?.displayName, mod.displayName].filter(Boolean).join(' - ');
    }
    if (level === 'chapter') {
      return [nameOf(mod.parentModuleId), mod.displayName].filter(Boolean).join(' - ');
    }
    return mod.displayName;
  }, [value, modules]);

  // Close on click outside — the dropdown lives in a portal, so it isn't a DOM
  // descendant of the container; check both refs.
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const t = e.target as Node;
      const inContainer = containerRef.current?.contains(t);
      const inDropdown = dropdownRef.current?.contains(t);
      if (!inContainer && !inDropdown) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Anchor the portaled dropdown to the input and keep it positioned while open
  // (re-measure on scroll/resize). `true` capture catches scrolls on any ancestor.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const update = () => {
      const el = containerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setPos({ left: r.left, top: r.bottom + 2, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen]);

  const handleSelect = (moduleId: string) => {
    onChange(moduleId);
    setQuery('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('');
    setQuery('');
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className={`topic-selector ${className || ''}`}>
      <div className="topic-selector-input-row">
        <input
          type="text"
          className="topic-selector-input"
          value={isOpen ? query : (selectedLabel || query)}
          onMouseDown={(e) => { if (blocked) { e.preventDefault(); onBlockedInteraction?.(); } }}
          onChange={(e) => { if (blocked) return; setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { if (blocked) { onBlockedInteraction?.(); return; } setIsOpen(true); }}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={blocked}
        />
        {value && (
          <button className="topic-selector-clear" onClick={handleClear} title="Clear selection">
            ×
          </button>
        )}
      </div>

      {isOpen && !disabled && pos && createPortal(
        <div
          ref={dropdownRef}
          className="topic-selector-dropdown"
          style={{ position: 'fixed', left: pos.left, top: pos.top, width: pos.width, right: 'auto' }}
        >
          {filtered.length === 0 && (
            <div className="topic-selector-empty">No results</div>
          )}
          {filtered.map(group => {
            const nested = group.module ? 'nested' : '';
            return (
              <div key={group.module?.id ?? group.chapters[0]?.chapter.id}>
                {group.module && (
                  <div
                    className={`topic-selector-module ${value === group.module.id ? 'selected' : ''} ${canSelect(group.module) ? '' : 'unselectable'}`}
                    // These options are plain divs, so a click inside a
                    // surrounding <label> is forwarded to that label's control
                    // instead of selecting. Suppressing the mousedown default
                    // stops the forwarding and keeps focus in the search box.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { if (canSelect(group.module!)) handleSelect(group.module!.id); }}
                  >
                    <span className="topic-selector-module-name">{group.module.displayName}</span>
                    <span className="topic-selector-module-badge">module</span>
                  </div>
                )}
                {group.chapters.map(cg => (
                  <div key={cg.chapter.id}>
                    <div
                      className={`topic-selector-chapter ${nested} ${value === cg.chapter.id ? 'selected' : ''} ${canSelect(cg.chapter) ? '' : 'unselectable'}`}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { if (canSelect(cg.chapter)) handleSelect(cg.chapter.id); }}
                    >
                      <span className="topic-selector-chapter-name">{cg.chapter.displayName}</span>
                      <span className="topic-selector-chapter-badge">chapter</span>
                    </div>
                    {cg.topics.map(topic => (
                      <div
                        key={topic.id}
                        className={`topic-selector-topic ${nested} ${value === topic.id ? 'selected' : ''}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleSelect(topic.id)}
                      >
                        {topic.displayName}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}
